import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Wand2, 
  Loader2,
  CheckCircle,
  AlertCircle,
  Code,
  FileJson,
  Upload,
  Sparkles,
  ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GeneratorPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [publishing, setPublishing] = useState(false);
  
  const [formData, setFormData] = useState({
    api_docs_url: '',
    service_name: '',
    industry: '',
    category: '',
    additional_notes: ''
  });

  const industries = [
    'Construction', 'CRM', 'Healthcare', 'Finance', 'Logistics',
    'Communication', 'Developer Tools', 'Marketing', 'E-commerce',
    'Human Resources', 'Data & BI', 'Other'
  ];

  const handleGenerate = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please sign in to generate connectors');
      return;
    }
    
    setGenerating(true);
    setResult(null);
    
    try {
      const response = await axios.post(`${API}/generate/connector`, formData);
      setResult(response.data);
      
      if (response.data.status === 'completed') {
        toast.success('Connector generated successfully!');
      } else if (response.data.status === 'failed') {
        toast.error('Generation failed. Please try again.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handlePublish = async () => {
    if (!result?.id) return;
    
    setPublishing(true);
    try {
      await axios.post(`${API}/generate/connector/${result.id}/publish`);
      toast.success('Connector submitted for review!');
      navigate('/community');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="pt-20 min-h-screen">
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <Badge className="mb-4 bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-400 border-purple-500/30">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            AI Powered
          </Badge>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">
            Connector Generator
          </h1>
          <p className="text-lg text-slate-400">
            Generate a complete Pipeyard connector from any API documentation using AI
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <div className="bg-card border border-white/5 rounded-xl p-6">
            <h2 className="font-heading text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-primary" />
              Generate Connector
            </h2>
            
            <form onSubmit={handleGenerate} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">
                  API Documentation URL *
                </label>
                <Input
                  value={formData.api_docs_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, api_docs_url: e.target.value }))}
                  placeholder="https://docs.example.com/api"
                  type="url"
                  required
                  className="bg-secondary border-border"
                  data-testid="api-docs-url-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">
                  Service Name *
                </label>
                <Input
                  value={formData.service_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, service_name: e.target.value }))}
                  placeholder="e.g., Monday.com, Notion, Calendly"
                  required
                  className="bg-secondary border-border"
                  data-testid="service-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">
                    Industry *
                  </label>
                  <Select 
                    value={formData.industry} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, industry: v }))}
                  >
                    <SelectTrigger className="bg-secondary border-border" data-testid="industry-select">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {industries.map((ind) => (
                        <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">
                    Category *
                  </label>
                  <Input
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    placeholder="e.g., Project Management"
                    required
                    className="bg-secondary border-border"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">
                  Additional Notes (Optional)
                </label>
                <Textarea
                  value={formData.additional_notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, additional_notes: e.target.value }))}
                  placeholder="Any specific actions or features you want included..."
                  className="bg-secondary border-border min-h-20"
                />
              </div>

              <Button
                type="submit"
                className="w-full glow-primary bg-primary hover:bg-primary/90"
                disabled={generating || !formData.api_docs_url || !formData.service_name || !formData.industry}
                data-testid="generate-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5 mr-2" />
                    Generate Connector
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Result */}
          <div className="space-y-6">
            {result ? (
              <>
                {/* Status Card */}
                <div className={`bg-card border rounded-xl p-6 ${
                  result.status === 'completed' 
                    ? 'border-emerald-500/30' 
                    : result.status === 'failed'
                    ? 'border-red-500/30'
                    : 'border-amber-500/30'
                }`}>
                  <div className="flex items-center gap-3 mb-4">
                    {result.status === 'completed' ? (
                      <CheckCircle className="w-6 h-6 text-emerald-400" />
                    ) : result.status === 'failed' ? (
                      <AlertCircle className="w-6 h-6 text-red-400" />
                    ) : (
                      <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
                    )}
                    <h3 className="font-heading text-lg font-semibold text-white capitalize">
                      {result.status === 'completed' ? 'Generation Complete' : 
                       result.status === 'failed' ? 'Generation Failed' : 'Processing...'}
                    </h3>
                  </div>
                  
                  {result.errors?.length > 0 && (
                    <div className="bg-red-950/30 border border-red-900/50 rounded-lg p-4 mb-4">
                      <p className="text-red-400 text-sm">{result.errors.join(', ')}</p>
                    </div>
                  )}

                  {result.status === 'completed' && (
                    <Button
                      onClick={handlePublish}
                      disabled={publishing}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid="publish-btn"
                    >
                      {publishing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Publishing...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Submit for Review
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {/* Generated Connector Preview */}
                {result.generated_connector && (
                  <div className="bg-card border border-white/5 rounded-xl overflow-hidden">
                    <div className="px-6 py-4 border-b border-white/5 flex items-center gap-2">
                      <FileJson className="w-5 h-5 text-primary" />
                      <h3 className="font-semibold text-white">Generated Connector</h3>
                    </div>
                    <div className="p-4">
                      <div className="mb-4">
                        <h4 className="text-sm text-slate-400 mb-1">Service</h4>
                        <p className="text-white font-semibold">{result.service_name}</p>
                      </div>
                      
                      <div className="mb-4">
                        <h4 className="text-sm text-slate-400 mb-1">Description</h4>
                        <p className="text-slate-300 text-sm">{result.generated_connector.description}</p>
                      </div>
                      
                      <div className="mb-4">
                        <h4 className="text-sm text-slate-400 mb-2">Actions ({result.generated_connector.actions?.length || 0})</h4>
                        <div className="space-y-2">
                          {result.generated_connector.actions?.map((action, index) => (
                            <div key={index} className="bg-secondary/50 rounded-lg p-3">
                              <code className="text-primary text-sm">{action.name}</code>
                              <p className="text-slate-400 text-xs mt-1">{action.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* PCS Schema Preview */}
                {result.pcs_schema && (
                  <div className="bg-card border border-white/5 rounded-xl overflow-hidden">
                    <div className="px-6 py-4 border-b border-white/5 flex items-center gap-2">
                      <Code className="w-5 h-5 text-accent" />
                      <h3 className="font-semibold text-white">PCS Schema</h3>
                      <Badge variant="outline" className="ml-auto border-slate-700 text-slate-400">
                        v{result.pcs_schema.pcs_version}
                      </Badge>
                    </div>
                    <div className="p-4 max-h-64 overflow-auto">
                      <pre className="text-xs text-slate-300 font-mono">
                        {JSON.stringify(result.pcs_schema, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-card border border-white/5 rounded-xl p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-secondary mx-auto mb-6 flex items-center justify-center">
                  <Wand2 className="w-8 h-8 text-slate-500" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-2">
                  AI-Powered Generation
                </h3>
                <p className="text-slate-400 text-sm max-w-sm mx-auto">
                  Enter an API documentation URL and let AI automatically generate a complete 
                  Pipeyard connector with actions, schemas, and documentation.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneratorPage;
